import React, { useEffect, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  ListItem,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import AccountIcon from '@material-ui/icons/AccountCircle';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  Link,
  sidebarConfig,
  SidebarItem,
  useSidebarOpenState,
} from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { BackstageTheme } from '@backstage/theme';
import { useNavigate } from 'react-router-dom';
import { useEpDevPAppContext } from '@solace-labs/backstage-plugin-ep-devp';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';

const useStyles = makeStyles<BackstageTheme>(theme => ({
  drawer: {
    width: sidebarConfig.drawerWidthOpen,
    borderRadius: 0,
    background: theme.palette.background.paper,
  },
  menu: {
    padding: 0,
    '&:focus': { outline: 'none' },
  },
  menuHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary,
    padding: theme.spacing(0.5, 2),
  },
  menuTitle: {
    fontSize: 18,
    fontWeight: 500,
    overflow: 'hidden',
    padding: theme.spacing(0.5, 1),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuSubtitle: {
    color: theme.palette.textSubtle,
  },
  menuItem: {
    color: theme.palette.text.primary,
  },
  submenu: {
    minWidth: '24ch',
    borderRadius: 0,
    background: theme.palette.background.paper,
  },
}));

const OrganizationMenuItem = ({ onSelect }: { onSelect: () => void }) => {
  const classes = useStyles();
  const { t } = useServiceRegistryTranslation();
  const [appContext, dispatchAppContext] = useEpDevPAppContext();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (appContext.organizationEntityIdList.length <= 1) return null;

  return (
    <>
      <MenuItem
        className={classes.menuItem}
        onClick={event => {
          setAnchor(event.currentTarget);
          setOpen(true);
        }}
      >
        <Typography variant="body1" noWrap>
          {appContext.organizationDisplayName}
        </Typography>
        <Box display="flex" ml="auto" mr={-1}>
          {open ? (
            <ChevronLeftIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </Box>
      </MenuItem>
      <Popper anchorEl={anchor} open={open} placement="right-end" transition>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper className={classes.submenu} elevation={4}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList className={classes.menu}>
                  <ListItem>
                    <Typography variant="subtitle2">
                      {t('navigation.switchOrganization')}
                    </Typography>
                  </ListItem>
                  <Divider />
                  {appContext.organizationEntityIdList.map(organization => (
                    <MenuItem
                      key={organization.id}
                      onClick={() => {
                        dispatchAppContext({
                          type: 'SET_ORGANIZATION_ID',
                          organizationId: organization.id,
                        });
                        setOpen(false);
                        onSelect();
                      }}
                    >
                      {organization.displayName}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export const ServiceRegistryUserAccountSidebarItem = ({
  title,
}: {
  title?: string;
}) => {
  const classes = useStyles();
  const { t } = useServiceRegistryTranslation();
  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();
  const { isOpen: isSidebarOpen } = useSidebarOpenState();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string>();

  useEffect(() => {
    let active = true;
    void identityApi.getProfileInfo().then(profile => {
      if (active) setDisplayName(profile.displayName);
    });
    return () => {
      active = false;
    };
  }, [identityApi]);

  const drawerWidth = isSidebarOpen
    ? sidebarConfig.drawerWidthOpen
    : sidebarConfig.drawerWidthClosed;

  return (
    <>
      <SidebarItem
        aria-owns={open ? 'service-registry-user-menu' : undefined}
        aria-haspopup="true"
        icon={AccountIcon}
        text={title ?? t('navigation.account')}
        onClick={() => setOpen(true)}
      />
      <Popper
        id={open ? 'service-registry-user-menu' : undefined}
        anchorEl={document.body}
        open={open}
        placement="top-start"
        modifiers={{
          offset: { enabled: true, offset: `${drawerWidth}, -100%p - 1` },
        }}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper className={classes.drawer} elevation={4}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList
                  className={classes.menu}
                  onKeyDown={event => {
                    if (event.key === 'Escape') setOpen(false);
                  }}
                >
                  <ListItem className={classes.menuHeader}>
                    <AccountIcon fontSize="large" />
                    <Typography className={classes.menuTitle} variant="h5">
                      {displayName}
                      <br />
                      <Typography
                        className={classes.menuSubtitle}
                        variant="caption"
                      >
                        {title}
                      </Typography>
                    </Typography>
                  </ListItem>
                  <Divider />
                  <MenuItem
                    className={classes.menuItem}
                    component={Link}
                    to="/settings/general"
                    onClick={() => setOpen(false)}
                  >
                    {t('navigation.settings')}
                  </MenuItem>
                  <OrganizationMenuItem
                    onSelect={() => {
                      setOpen(false);
                      navigate('/');
                    }}
                  />
                  <Divider />
                  <MenuItem
                    className={classes.menuItem}
                    onClick={() => void identityApi.signOut()}
                  >
                    {t('navigation.signOut')}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
